import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
    UserPermissions,
    UserRole,
    UserSessionStatus,
} from '../models/user.model';

import envConfig from '../services/env-config';

type User = {
    aam: boolean;
    customerCanDemo: boolean;
    formattedName: string;
    redirect?: string;
    username: string;
    id: number;
    email: string;
    name: string;
    is_beta_user: boolean;
    customerId: number;
    customerName: string;
    role: UserRole;
    permissions: UserPermissions[];
    isBetaUser: boolean;
    isBetaMode?: boolean;
    multilanguage_enabled?: boolean;
    [key: string]: any;
};

type Session = {
    user?: User;
    expires?: string;
    [key: string]: any;
};

export const fetchSession = async (): Promise<Session | null> => {
    if (envConfig.ENVIRONMENT === 'test') {
        const sessionStr = localStorage.getItem('session');
        return sessionStr ? JSON.parse(sessionStr) : null;
    }

    try {
        const res = await fetch(`${envConfig.AUTH_SERVICE}/session`, {
            credentials: 'include',
        });

        if (!res.ok) {
            console.warn('Session fetch failed with status:', res.status);
            return null;
        }

        return await res.json();
    } catch (err) {
        console.error('Failed to fetch session:', err);
        return null;
    }
};

export function useAuthSession() {
    const queryClient = useQueryClient();

    const { data: session, status: queryStatus } = useQuery({
        queryKey: ['authSession'],
        queryFn: fetchSession,
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        retry: false,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (session?.user) {
            if (!session.user.aam) {
                window.analytics.identify(session.user.name, {
                    email: session.user.email,
                    customer_name: session.user.customerName,
                    customerId: session.user.customerId,
                    internal_user: false,
                });
            }

            Sentry.setUser({
                username: session.user.name,
                customerName: session.user.customerName,
            });
        }
    }, [session?.user]);

    // Check for session expiration inside the hook
    const now = Date.now();
    const expiration = session?.expires
        ? new Date(session.expires).getTime()
        : null;

    const isExpired = expiration !== null && expiration <= now;
    const updateSession = (
        updater: (prev: Session | null) => Session | null,
    ) => {
        queryClient.setQueryData(['authSession'], updater);
    };

    if (isExpired) {
        // Invalidate the session cache
        queryClient.invalidateQueries({ queryKey: ['authSession'] });
        // Redirect to auth service for re-authentication
        window.location.href = envConfig.AUTH_SERVICE_SIGNIN;

        // Treat session as unauthenticated
        return {
            session: null,
            status: UserSessionStatus.UNAUTHENTICATED,
            user: null,
            isAuthenticated: false,
            updateSession,
        };
    }

    const status: UserSessionStatus =
        queryStatus === 'pending'
            ? UserSessionStatus.LOADING
            : session?.user
            ? UserSessionStatus.AUTHENTICATED
            : UserSessionStatus.UNAUTHENTICATED;

    const extendedUser = session?.user
        ? {
              ...session?.user,
              isInternalAampeAdmin: session.user?.aam || false,
              isBetaMode: session?.user?.is_beta_user ? true : false,
              customerId: session.user?.customerId,
          }
        : null;

    return {
        session,
        status,
        customerId: extendedUser?.customerId,
        user: extendedUser,
        isAuthenticated: status === 'authenticated',
        updateSession,
    };
}
